import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import {
  pickInvoiceStatusLabel,
  type U3125InvoiceFormRow,
  type U3125SourceUsageConsumptionGridRow,
} from "../../../../U3125Service";
import { NS, u3125FormatCurrency } from "../../../../U3125";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";
import U3125SourceUsageSummaryBanner from "./U3125SourceUsageSummaryBanner";

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "targetInvoiceNumber",
      label: t("colTargetInvoiceNumber", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[14%]",
      type: "number",
      align: "left",
      display: (_c, value) => (value == null ? "—" : String(value)),
    },
    {
      name: "targetInvoiceDate",
      label: t("colTargetInvoiceDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[18%]",
      type: "string",
      align: "left",
      display: (_c, value) => {
        if (value == null || value === "") return "";
        const s = String(value);
        return s.length >= 10 ? s.slice(0, 10) : s;
      },
    },
    {
      name: "targetInvoiceStatus",
      label: t("colTargetInvoiceStatus", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[22%]",
      type: "string",
      align: "left",
      display: (_c, value) => pickInvoiceStatusLabel(t, value == null ? "" : String(value)),
    },
    {
      name: "amount",
      label: t("colConsumptionAmount", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[14%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
  ];
}

interface U3125SourceUsageTabBodyProps {
  parentFormHandlers: U3125FactureNestedParentHandlers & { getData: () => U3125InvoiceFormRow | null };
}

const U3125SourceUsageTabBody: React.FC<U3125SourceUsageTabBodyProps> = ({ parentFormHandlers }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridStateHandlers = useU2000GridStateHandlers<U3125SourceUsageConsumptionGridRow>();

  const invoiceData = parentFormHandlers.getData?.() ?? null;
  const summary = invoiceData?.sourceUsageSummary ?? null;
  const consumptionsKey = (summary?.consumptions ?? [])
    .map((row) => `${row.targetInvoiceId}|${row.amount}|${row.targetInvoiceNumber}`)
    .join(";");

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const list = (summary?.consumptions ?? []).map((row) => ({
      ...row,
      id: row.targetInvoiceId,
    })) as U3125SourceUsageConsumptionGridRow[];
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [t, consumptionsKey]);

  if (!summary) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">{t("msgSourceUsageUnavailable", { ns: NS })}</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <U3125SourceUsageSummaryBanner
        summary={summary}
        documentNature={invoiceData?.documentNature ?? "CREDIT"}
        t={t}
      />

      {summary.consumptions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("msgSourceUsageEmpty", { ns: NS })}</p>
      ) : (
        <U2000DetailTable
          gridStateHandlers={gridStateHandlers}
          rowClickFn={(row) => {
            const targetId = (row as U3125SourceUsageConsumptionGridRow).targetInvoiceId?.trim();
            if (targetId) navigate(`/U3125/${targetId}`);
          }}
          allowRowSelection={false}
          label={t("lblSourceUsageConsumptionsGrid", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        />
      )}
    </div>
  );
};

export default U3125SourceUsageTabBody;
